/**
 * TUVI - Modal Registrar Pago
 * Modal para registrar pagos de facturas con evidencia obligatoria
 */

import React, { useState, useRef, useEffect } from 'react';
import { useNotification } from '../../context/NotificationContext';
import client from '../../services/graphqlClient';
import { UPLOAD_URL } from '../../config';
import './ModalRegistrarPago.css';

const ModalRegistrarPago = ({ show, onClose, facturas, onPagoRegistrado }) => {
  const { success, error } = useNotification();
  const fileInputRef = useRef(null);
  const [guardando, setGuardando] = useState(false);
  const [montosPago, setMontosPago] = useState({});
  const [ivaConfig, setIvaConfig] = useState({}); // { [facturaId]: { tieneIva, porcentaje, retenciones: { [retId]: { aplicar, base } } } }
  const [formData, setFormData] = useState({
    fecha_pago: new Date().toLocaleDateString('en-CA', { timeZone: 'America/Bogota' }),
    metodo_pago: 'transferencia',
    referencia: '',
    banco: '',
    notas: ''
  });
  const [archivoEvidencia, setArchivoEvidencia] = useState(null);
  const [pdfGenerado, setPdfGenerado] = useState(null); // { pdf_key } after successful payment

  // Inicializar montos y config (IVA + retenciones) cuando cambian las facturas.
  // El monto inicial ya descuenta TODAS las retenciones del proveedor por defecto.
  useEffect(() => {
    if (facturas && facturas.length > 0) {
      const montosIniciales = {};
      const ivaIniciales = {};
      facturas.forEach(f => {
        // Por defecto se usa la retención que ya quedó registrada en la factura (al
        // crearla o en su primer abono). Solo si no tiene ninguna se calcula con la
        // configuración actual del proveedor.
        const modo = (parseFloat(f.total_retenciones) || 0) > 0 || (parseFloat(f.monto_pagado) || 0) > 0 ? 'registrada' : 'recalcular';
        const cfg = { tieneIva: false, porcentaje: 19, retenciones: {}, modo };
        ivaIniciales[f.id] = cfg;
        montosIniciales[f.id] = calcularDesgloseConConfig(f, cfg).maxPagable;
      });
      setMontosPago(montosIniciales);
      setIvaConfig(ivaIniciales);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facturas]);

  const esRetIvaCodigo = (codigo) => codigo === 'IVA' || codigo === 'IVA_REGIMEN_SIMPLE';

  // Lista de retenciones del proveedor para esta factura. Si el backend aún no
  // trae la lista, se arma una sola desde los campos prov_* (compatibilidad).
  const getRetList = (factura) => {
    if (factura.retenciones && factura.retenciones.length > 0) return factura.retenciones;
    const pct = parseFloat(factura.prov_porcentaje_retencion || factura.porcentaje_retencion) || 0;
    if (pct > 0) {
      return [{
        retencion_id: -1,
        porcentaje_retencion: pct,
        base_retencion: parseFloat(factura.prov_base_retencion || factura.base_retencion) || 0,
        tipo_retencion_codigo: factura.prov_tipo_retencion || factura.tipo_retencion_codigo || null,
        tipo_retencion_nombre: 'Retención',
      }];
    }
    return [];
  };

  // Calcula IVA + TODAS las retenciones del proveedor (cada una con su base).
  const calcularDesgloseConConfig = (factura, config) => {
    const monto = parseFloat(factura.monto) || 0;
    const montoPagadoPrevio = parseFloat(factura.monto_pagado) || 0;
    const retList = getRetList(factura);

    // Con abonos previos la retención quedó fija (se descontó en el primer pago y
    // quedó guardada en la factura): no se recalcula con la configuración actual del
    // proveedor, que puede haber cambiado. Si se recalculara, el "disponible" no
    // coincidiría con el saldo de la lista. Sin abonos, por defecto también se
    // respeta la registrada, pero se puede recalcular.
    const bloqueada = montoPagadoPrevio > 0;
    const retFuenteReg = parseFloat(factura.retencion_fuente) || 0;
    const retIvaReg = parseFloat(factura.retencion_iva) || 0;
    const retOtrasReg = parseFloat(factura.otras_retenciones) || 0;
    const retRegistrada = retFuenteReg + retIvaReg + retOtrasReg;
    if (bloqueada || config.modo === 'registrada') {
      const subtotalFact = (parseFloat(factura.subtotal) || 0) > 0 ? parseFloat(factura.subtotal) : monto;
      const ivaFact = Math.max(monto - subtotalFact, 0);
      const pctDe = (valor, base) => (base > 0 ? Math.round((valor / base) * 10000) / 100 : 0);
      const nombrePrincipal = retList.find(r => !esRetIvaCodigo(r.tipo_retencion_codigo))?.tipo_retencion_nombre || 'Retención';
      const fila = (id, nombre, esIva, base, valor) => ({
        retencion_id: id, nombre, codigo: esIva ? 'IVA' : null, pct: pctDe(valor, base), umbral: 0, esIva,
        aplicar: true, base, baseDefault: base, baseManual: false, valor, registrada: true,
      });
      const retenciones = [];
      if (retFuenteReg > 0) retenciones.push(fila('reg-fuente', nombrePrincipal, false, subtotalFact, retFuenteReg));
      if (retIvaReg > 0) retenciones.push(fila('reg-iva', 'Retención de IVA', true, ivaFact, retIvaReg));
      if (retOtrasReg > 0) retenciones.push(fila('reg-otras', 'Otras retenciones', false, subtotalFact, retOtrasReg));
      const totalRetencion = retRegistrada;
      const totalPagar = Math.max(monto - totalRetencion, 0);
      // Redondeado a pesos: el backend ajusta los centavos de diferencia.
      const maxPagable = Math.max(Math.round(totalPagar - montoPagadoPrevio), 0);
      return {
        monto, subtotalCalculado: subtotalFact, ivaValor: ivaFact, ivaPct: parseFloat(factura.porcentaje_iva) || 0, tieneIva: ivaFact > 0,
        retenciones, totalRetencion, totalPagar, maxPagable, montoPagadoPrevio,
        tieneRetenciones: retenciones.length > 0 || retList.length > 0,
        modo: 'registrada', bloqueada, puedeRecalcular: !bloqueada && retList.length > 0, retRegistrada,
      };
    }

    // Subtotal / IVA (si la factura tiene IVA marcado)
    const tieneIva = !!config.tieneIva;
    let subtotalCalculado = monto;
    let ivaValor = 0;
    if (tieneIva && config.porcentaje > 0) {
      subtotalCalculado = Math.round(monto / (1 + config.porcentaje / 100));
      ivaValor = monto - subtotalCalculado;
    }

    const retCfg = config.retenciones || {};
    const retenciones = retList.map(r => {
      const cfg = retCfg[r.retencion_id] || {};
      const aplicar = cfg.aplicar !== false; // por defecto se aplica
      const esIva = esRetIvaCodigo(r.tipo_retencion_codigo);
      // Base por defecto: retención sobre IVA -> valor del IVA (implícito 19% si no
      // hay IVA marcado); las demás -> subtotal (si hay IVA) o el valor de la factura.
      let baseDefault;
      if (esIva) baseDefault = tieneIva ? ivaValor : (monto - Math.round(monto / 1.19));
      else baseDefault = tieneIva ? subtotalCalculado : monto;
      const baseManual = cfg.base !== undefined && cfg.base !== null && cfg.base !== '';
      const base = baseManual ? (parseFloat(cfg.base) || 0) : baseDefault;
      const pct = parseFloat(r.porcentaje_retencion) || 0;
      const umbral = parseFloat(r.base_retencion) || 0;
      let valor = 0;
      if (aplicar && pct > 0 && base >= umbral) valor = Math.round(base * (pct / 100));
      return {
        retencion_id: r.retencion_id, nombre: r.tipo_retencion_nombre || 'Retención',
        codigo: r.tipo_retencion_codigo || null, pct, umbral, esIva,
        aplicar, base, baseDefault, baseManual, valor,
      };
    });

    const totalRetencion = retenciones.reduce((s, d) => s + d.valor, 0);
    const totalPagar = Math.max(monto - totalRetencion, 0);
    const maxPagable = Math.max(totalPagar - montoPagadoPrevio, 0);

    return {
      monto, subtotalCalculado, ivaValor, ivaPct: config.porcentaje, tieneIva,
      retenciones, totalRetencion, totalPagar, maxPagable, montoPagadoPrevio,
      tieneRetenciones: retList.length > 0,
      modo: 'recalcular', bloqueada: false, puedeUsarRegistrada: retRegistrada > 0, retRegistrada,
    };
  };

  // Cambia entre usar la retención registrada en la factura y recalcularla con la
  // configuración actual del proveedor (solo en facturas sin abonos).
  const handleModoRetencion = (facturaId, modo) => {
    const previo = ivaConfig[facturaId] || { tieneIva: false, porcentaje: 19, retenciones: {} };
    aplicarConfig(facturaId, { ...previo, modo });
  };

  // Recalcula el monto pagable al cambiar cualquier parte de la config
  const aplicarConfig = (facturaId, nuevoConfig) => {
    setIvaConfig(prev => ({ ...prev, [facturaId]: nuevoConfig }));
    const factura = facturas.find(f => f.id === facturaId);
    if (factura) {
      const desglose = calcularDesgloseConConfig(factura, nuevoConfig);
      setMontosPago(prev => ({ ...prev, [facturaId]: desglose.maxPagable }));
    }
  };

  const handleIvaToggle = (facturaId, tieneIva) => {
    const previo = ivaConfig[facturaId] || { tieneIva: false, porcentaje: 19, retenciones: {} };
    aplicarConfig(facturaId, { ...previo, tieneIva });
  };

  const handleIvaPorcentaje = (facturaId, porcentaje) => {
    const previo = ivaConfig[facturaId] || { tieneIva: true, porcentaje: 19, retenciones: {} };
    aplicarConfig(facturaId, { ...previo, porcentaje: parseFloat(porcentaje) || 0 });
  };

  const handleAplicarRetencion = (facturaId, retId, aplicar) => {
    const previo = ivaConfig[facturaId] || { tieneIva: false, porcentaje: 19, retenciones: {} };
    const rets = { ...(previo.retenciones || {}) };
    rets[retId] = { ...(rets[retId] || {}), aplicar };
    aplicarConfig(facturaId, { ...previo, retenciones: rets });
  };

  const handleBaseRetencion = (facturaId, retId, valor) => {
    const previo = ivaConfig[facturaId] || { tieneIva: false, porcentaje: 19, retenciones: {} };
    const rets = { ...(previo.retenciones || {}) };
    rets[retId] = { ...(rets[retId] || {}), base: valor === '' ? null : valor };
    aplicarConfig(facturaId, { ...previo, retenciones: rets });
  };

  // Wrapper que usa el ivaConfig actual del estado
  const calcularDesglose = (factura) => {
    const config = ivaConfig[factura.id] || { tieneIva: false, porcentaje: 19, retenciones: {} };
    return calcularDesgloseConConfig(factura, config);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleMontoChange = (facturaId, valor) => {
    setMontosPago(prev => ({
      ...prev,
      [facturaId]: parseFloat(valor) || 0
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validar tipo
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        error('Solo se permiten imágenes (JPG, PNG, GIF, WebP) y PDF');
        e.target.value = '';
        return;
      }
      // Validar tamaño (10MB)
      if (file.size > 10 * 1024 * 1024) {
        error('El archivo es muy grande. Máximo 10MB');
        e.target.value = '';
        return;
      }
      setArchivoEvidencia(file);
    }
  };

  const quitarArchivo = () => {
    setArchivoEvidencia(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Pegar imagen desde clipboard (Ctrl+V / recorte de pantalla)
  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const blob = item.getAsFile();
        if (blob) {
          if (blob.size > 10 * 1024 * 1024) {
            error('La imagen pegada es muy grande. Máximo 10MB');
            return;
          }
          const ext = blob.type.split('/')[1] || 'png';
          const file = new File([blob], `comprobante_${Date.now()}.${ext}`, { type: blob.type });
          setArchivoEvidencia(file);
        }
        return;
      }
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const formatMoney = (valor) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(valor || 0);
  };

  const calcularTotalPago = () => {
    return Object.values(montosPago).reduce((sum, monto) => sum + (parseFloat(monto) || 0), 0);
  };

  // Tope real que se puede pagar por factura: lo que falta por pagar al proveedor
  // (Total a pagar al proveedor − abonos previos). NO usamos el campo saldo_pendiente
  // almacenado porque puede estar desactualizado; maxPagable se deriva del valor real
  // de la factura y la retención.
  const topePagableFactura = (factura) => calcularDesglose(factura).maxPagable;

  const validarMontos = () => {
    for (const factura of facturas) {
      const montoPago = parseFloat(montosPago[factura.id]) || 0;
      if (montoPago < 0) {
        error('Los montos deben ser positivos');
        return false;
      }
      const tope = topePagableFactura(factura);
      // Tolerancia de 1 peso por redondeos
      if (montoPago > tope + 1) {
        error(`El monto de la factura #${factura.numero_factura} (${formatMoney(montoPago)}) supera el saldo disponible (${formatMoney(tope)}). Bájalo a ${formatMoney(tope)} o menos para registrar el pago.`);
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validar evidencia obligatoria
    if (!archivoEvidencia) {
      error('Debes adjuntar una evidencia del pago (foto o documento)');
      return;
    }

    // Validar que al menos hay un monto
    const totalPago = calcularTotalPago();
    if (totalPago <= 0) {
      error('Debes ingresar al menos un monto de pago');
      return;
    }

    // Validar montos no excedan saldos
    if (!validarMontos()) {
      return;
    }

    setGuardando(true);

    try {
      const formDataToSend = new FormData();

      // Datos del pago
      formDataToSend.append('fecha_pago', formData.fecha_pago);
      formDataToSend.append('metodo_pago', formData.metodo_pago);
      if (formData.referencia) formDataToSend.append('referencia', formData.referencia);
      if (formData.banco) formDataToSend.append('banco', formData.banco);
      if (formData.notas) formDataToSend.append('notas', formData.notas);

      // Facturas con montos - separar en arrays paralelos como espera el backend
      const facturasConMonto = facturas.filter(f => (parseFloat(montosPago[f.id]) || 0) > 0);
      const facturas_ids = facturasConMonto.map(f => f.id);
      const montos = facturasConMonto.map(f => parseFloat(montosPago[f.id]) || 0);
      // Retención con la que se paga cada factura. El backend la guarda en la factura
      // en su primer abono, para que el saldo de la lista coincida con lo retenido.
      const retencionesPago = facturasConMonto
        .filter(f => (parseFloat(f.monto_pagado) || 0) === 0)
        .map(f => {
          const d = calcularDesglose(f);
          let fuente = 0, iva = 0;
          for (const r of d.retenciones) {
            if (!r.aplicar || r.valor <= 0) continue;
            if (r.esIva) iva += r.valor; else fuente += r.valor;
          }
          return { factura_id: f.id, retencion_fuente: Math.round(fuente), retencion_iva: Math.round(iva) };
        });

      // 1. Registrar pago via GraphQL
      const gqlR = await client.mutate(`mutation($input: RegistrarPagoInput!) { registrarPago(input: $input) { success message pagos { pago_id factura_id numero_factura monto_pagado } total_pagado } }`, {
        input: {
          facturas_ids: facturas_ids,
          montos: montos,
          retenciones: retencionesPago,
          fecha_pago: formData.fecha_pago,
          metodo_pago: formData.metodo_pago || null,
          referencia: formData.referencia || null,
          banco: formData.banco || null,
          notas: formData.notas || null,
        }
      });

      const data = gqlR.registrarPago;

      if (data.success) {
        // 2. Subir evidencia + generar PDF comprobante
        let pdfResult = null;
        if (archivoEvidencia) {
          const fd = new FormData();
          fd.append('evidencia', archivoEvidencia);
          // Enviar datos para generar PDF
          const provInfo = facturas[0] ? { nombre: facturas[0].proveedor_nombre, nit: facturas[0].proveedor_nit } : {};
          const facturasInfo = facturasConMonto.map(f => {
            const desglose = calcularDesglose(f);
            const montoPago = parseFloat(montosPago[f.id]) || 0;
            const montoPagadoPrevio = parseFloat(f.monto_pagado) || 0;
            const saldoAntesPago = parseFloat(f.saldo_real || f.saldo_pendiente || f.monto) || 0;
            const saldoDespuesPago = Math.max(0, saldoAntesPago - montoPago);
            // Desglose de retenciones aplicadas (para mostrarlas en el comprobante).
            const retAplicadas = desglose.retenciones
              .filter(r => r.aplicar && r.valor > 0)
              .map(r => ({ nombre: r.nombre, porcentaje: r.pct, base: r.base, valor: r.valor, es_iva: r.esIva }));
            // Sumar por tipo para los 3 campos que ya usa el PDF (iva vs fuente).
            let retenIva = 0;
            let retenFuente = 0;
            for (const r of desglose.retenciones) {
              if (!r.aplicar || r.valor <= 0) continue;
              if (r.esIva) retenIva += r.valor; else retenFuente += r.valor;
            }
            return {
              numero_factura: f.numero_factura,
              proyecto_nombre: f.proyecto_nombre,
              apartamento: f.apartamento || '',
              // monto = saldo pendiente ANTES del pago (no el monto total de la factura)
              monto: saldoAntesPago,
              // monto_pagado_actual = lo que se paga en este pago
              monto_pagado_actual: montoPago,
              monto_pagado_previo: montoPagadoPrevio,
              monto_total_factura: parseFloat(f.monto) || 0,
              retencion_fuente: retenFuente,
              retencion_iva: retenIva,
              otras_retenciones: 0,
              retenciones_detalle: retAplicadas,
              saldo_pendiente: saldoDespuesPago,
            };
          });
          fd.append('proveedor', JSON.stringify(provInfo));
          fd.append('facturas', JSON.stringify(facturasInfo));
          fd.append('pagoInfo', JSON.stringify({
            fecha_pago: formData.fecha_pago, metodo_pago: formData.metodo_pago,
            referencia: formData.referencia, banco: formData.banco, notas: formData.notas,
          }));
          const token = localStorage.getItem('tuvi_token');
          try {
            const uploadRes = await fetch(`${UPLOAD_URL}/pago-comprobante`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: fd });
            pdfResult = await uploadRes.json();
            // Guardar rutas del comprobante y PDF en los registros de pago
            if (pdfResult?.success && data.pagos?.length > 0) {
              for (const p of data.pagos) {
                try {
                  await client.mutate(
                    `mutation($id: Int!, $archivo: String, $ruta: String) { actualizarComprobantePago(id: $id, comprobante_archivo: $archivo, comprobante_ruta: $ruta) { success } }`,
                    { id: p.pago_id, archivo: pdfResult.comprobante_key || null, ruta: pdfResult.pdf_key || null }
                  );
                } catch {}
              }
            }
          } catch {}
        }

        const cantidadPagos = data.pagos?.length || 1;
        success(`Pago registrado: ${formatMoney(data.total_pagado)} en ${cantidadPagos} factura(s)`);

        if (pdfResult?.pdf_key) {
          setPdfGenerado(pdfResult);
        } else {
          resetForm();
          if (onPagoRegistrado) onPagoRegistrado();
        }
      } else {
        error('No se pudo registrar el pago: ' + (data.message || 'motivo desconocido'));
      }
    } catch (err) {
      console.error('Error al registrar pago:', err);
      const motivo = (err?.message || '').replace(/^Error:\s*/i, '').trim();
      error(motivo ? `No se pudo registrar el pago: ${motivo}` : 'Error al registrar el pago');
    } finally {
      setGuardando(false);
    }
  };

  const resetForm = () => {
    setFormData({
      fecha_pago: new Date().toLocaleDateString('en-CA', { timeZone: 'America/Bogota' }),
      metodo_pago: 'transferencia',
      referencia: '',
      banco: '',
      notas: ''
    });
    setArchivoEvidencia(null);
    setMontosPago({});
    setIvaConfig({});
    setPdfGenerado(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const descargarPDF = () => {
    if (!pdfGenerado?.pdf_key) return;
    const token = localStorage.getItem('tuvi_token');
    window.open(`${UPLOAD_URL}/pago-pdf/${encodeURIComponent(pdfGenerado.pdf_key)}?token=${token}`, '_blank');
  };

  const cerrarConPDF = () => {
    setPdfGenerado(null);
    resetForm();
    if (onPagoRegistrado) onPagoRegistrado();
  };

  if (!show || !facturas || facturas.length === 0) return null;

  const proveedor = facturas[0];
  // Total realmente pagable = suma de los topes por factura (ya descuenta la
  // retención que se está aplicando), para que coincida con lo que deja registrar.
  const totalSaldoReal = facturas.reduce((sum, f) => sum + topePagableFactura(f), 0);
  const totalRetenciones = facturas.reduce((sum, f) => sum + (f.total_retenciones || 0), 0);
  const totalPago = calcularTotalPago();

  // Vista de exito con PDF
  if (pdfGenerado) {
    return (
      <div className="modal-overlay">
        <div className="modal-content modal-registrar-pago" style={{ maxWidth: 480, textAlign: 'center', padding: '40px 30px' }}>
          <div style={{ fontSize: '3rem', marginBottom: 12 }}>&#9989;</div>
          <h2 style={{ color: '#059669', margin: '0 0 8px' }}>Pago Registrado</h2>
          <p style={{ color: '#64748b', fontSize: '0.9rem', margin: '0 0 24px' }}>El comprobante PDF fue generado exitosamente</p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={descargarPDF} style={{ padding: '10px 24px', background: '#059669', color: '#fff', border: 'none', borderRadius: 8, fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer' }}>
              Descargar PDF Comprobante
            </button>
            <button onClick={cerrarConPDF} style={{ padding: '10px 24px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 8, fontSize: '0.9rem', cursor: 'pointer' }}>
              Cerrar
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onPaste={handlePaste}>
      <div className="modal-content modal-registrar-pago">
        <div className="modal-header">
          <h2>💳 Registrar Pago</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-subheader">
          <div className="pago-proveedor-info">
            <span className="proveedor-nombre">{proveedor.proveedor_nombre}</span>
            {proveedor.proveedor_nit && (
              <span className="proveedor-nit">NIT: {proveedor.proveedor_nit}</span>
            )}
          </div>
          <div className="pago-resumen">
            <span className="resumen-facturas">{facturas.length} factura(s)</span>
            <span className="resumen-total">Total Pendiente: {formatMoney(totalSaldoReal)}</span>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {/* Lista de facturas a pagar */}
            <div className="facturas-pago-section">
              <h3>Facturas a Pagar</h3>
              <div className="facturas-pago-lista">
                {facturas.map(factura => {
                  const desglose = calcularDesglose(factura);
                  const config = ivaConfig[factura.id] || { tieneIva: false, porcentaje: 19 };
                  return (
                    <div key={factura.id} className="factura-pago-item">
                      <div className="factura-info">
                        <span className="factura-numero">#{factura.numero_factura}</span>
                        <span className="factura-saldo">
                          Valor factura: {formatMoney(desglose.monto)}
                        </span>
                        {factura.monto_pagado > 0 && (
                          <span className="factura-estado-parcial">
                            Abonado: {formatMoney(factura.monto_pagado)} · Pendiente: {formatMoney(desglose.maxPagable)}
                          </span>
                        )}
                      </div>

                      {/* Toggle IVA (solo al recalcular: la registrada ya trae su subtotal/IVA) */}
                      {desglose.modo !== 'registrada' && (
                      <div className="factura-iva-config">
                        <label className="iva-toggle">
                          <input
                            type="checkbox"
                            checked={config.tieneIva}
                            onChange={(e) => handleIvaToggle(factura.id, e.target.checked)}
                          />
                          <span>¿Factura con IVA?</span>
                        </label>
                        {config.tieneIva && (
                          <div className="iva-porcentaje-input">
                            <label>% IVA:</label>
                            <input
                              type="number"
                              value={config.porcentaje}
                              onChange={(e) => handleIvaPorcentaje(factura.id, e.target.value)}
                              min="0"
                              max="100"
                              step="1"
                            />
                            <span>%</span>
                          </div>
                        )}
                      </div>
                      )}

                      {/* Desglose IVA (si aplica) */}
                      {desglose.modo !== 'registrada' && config.tieneIva && (
                        <div className="factura-desglose">
                          <div className="desglose-line">
                            <span>Subtotal calculado (antes de IVA):</span>
                            <span>{formatMoney(desglose.subtotalCalculado)}</span>
                          </div>
                          <div className="desglose-line iva">
                            <span>IVA ({desglose.ivaPct}%):</span>
                            <span>{formatMoney(desglose.ivaValor)}</span>
                          </div>
                        </div>
                      )}

                      {/* Retención registrada en la factura (solo lectura) */}
                      {desglose.tieneRetenciones && desglose.modo === 'registrada' && (
                        <div className="factura-desglose" style={{ marginTop: 8 }}>
                          <div style={{ fontWeight: 600, fontSize: 13, color: '#475569', marginBottom: 6 }}>
                            Retención registrada en la factura
                          </div>
                          {desglose.retenciones.length === 0 && (
                            <div style={{ fontSize: 13, color: '#64748b', marginBottom: 6 }}>Sin retención registrada.</div>
                          )}
                          {desglose.retenciones.map(r => (
                            <div key={r.retencion_id} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
                              <span style={{ minWidth: 170 }}>{r.nombre} <strong>({r.pct}%)</strong></span>
                              <span style={{ fontSize: 12, color: '#64748b' }}>{r.esIva ? 'Base (IVA):' : 'Base:'} <strong style={{ color: '#1e293b' }}>{formatMoney(r.base)}</strong></span>
                              <span className="negativo" style={{ marginLeft: 'auto', color: '#dc2626', fontWeight: 600 }}>- {formatMoney(r.valor)}</span>
                            </div>
                          ))}
                          <div className="desglose-line total" style={{ borderTop: '1px solid #e2e8f0', paddingTop: 6, marginTop: 2 }}>
                            <span>Total a pagar al proveedor:</span>
                            <span className="total-valor">{formatMoney(desglose.totalPagar)}</span>
                          </div>
                          {desglose.bloqueada ? (
                            <small style={{ color: '#64748b', display: 'block', marginTop: 4 }}>
                              La factura ya tiene abonos: la retención quedó fija y no se recalcula.
                            </small>
                          ) : desglose.puedeRecalcular && (
                            <button type="button" className="btn-link-ret" onClick={() => handleModoRetencion(factura.id, 'recalcular')}>
                              Recalcular con la configuración actual del proveedor
                            </button>
                          )}
                        </div>
                      )}

                      {/* Retenciones del proveedor: una o varias, cada una con su base */}
                      {desglose.tieneRetenciones && desglose.modo !== 'registrada' && (
                        <div className="factura-desglose" style={{ marginTop: 8 }}>
                          <div style={{ fontWeight: 600, fontSize: 13, color: '#475569', marginBottom: 6 }}>
                            {desglose.retenciones.length > 1 ? `Retenciones (${desglose.retenciones.length})` : 'Retención'}
                          </div>
                          {desglose.retenciones.map(r => (
                            <div key={r.retencion_id} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
                              <label style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 170 }}>
                                <input
                                  type="checkbox"
                                  checked={r.aplicar}
                                  onChange={(e) => handleAplicarRetencion(factura.id, r.retencion_id, e.target.checked)}
                                />
                                <span>{r.nombre} <strong>({r.pct}%)</strong></span>
                              </label>
                              {r.aplicar && (
                                <>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                    <span style={{ fontSize: 12, color: '#64748b' }}>{r.esIva ? 'Base (IVA):' : 'Base:'}</span>
                                    {r.esIva ? (
                                      <span style={{ fontWeight: 600 }}>{formatMoney(r.base)}</span>
                                    ) : (
                                      <div className="input-subtotal-manual">
                                        <span className="currency-sm">$</span>
                                        <input
                                          type="number"
                                          value={r.baseManual ? r.base : r.baseDefault}
                                          onChange={(e) => handleBaseRetencion(factura.id, r.retencion_id, e.target.value)}
                                          min="0"
                                          step="any"
                                        />
                                        {r.baseManual && (
                                          <button
                                            type="button"
                                            className="btn-reset-subtotal"
                                            onClick={() => handleBaseRetencion(factura.id, r.retencion_id, '')}
                                            title="Restaurar base"
                                          >
                                            ↺
                                          </button>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                  <span className="negativo" style={{ marginLeft: 'auto', color: '#dc2626', fontWeight: 600 }}>- {formatMoney(r.valor)}</span>
                                  {r.umbral > 0 && r.base < r.umbral && (
                                    <small style={{ color: '#f59e0b', width: '100%' }}>
                                      La base ({formatMoney(r.base)}) es menor a la base mínima ({formatMoney(r.umbral)}); esta retención no se aplica.
                                    </small>
                                  )}
                                </>
                              )}
                            </div>
                          ))}
                          <div className="desglose-line total" style={{ borderTop: '1px solid #e2e8f0', paddingTop: 6, marginTop: 2 }}>
                            <span>Total a pagar al proveedor:</span>
                            <span className="total-valor">{formatMoney(desglose.totalPagar)}</span>
                          </div>
                          {desglose.puedeUsarRegistrada && (
                            <button type="button" className="btn-link-ret" onClick={() => handleModoRetencion(factura.id, 'registrada')}>
                              Usar la retención registrada en la factura ({formatMoney(desglose.retRegistrada)})
                            </button>
                          )}
                        </div>
                      )}

                      {/* Detalle de pagos anteriores (pago parcial) */}
                      {factura.monto_pagado > 0 && (
                        <div className="factura-pagos-previos">
                          <div className="pagos-previos-header">Historial de abonos</div>
                          <div className="pagos-previos-grid">
                            <div className="pp-row pp-header-row">
                              <span>Valor factura</span>
                              <span className="pp-val">{formatMoney(factura.monto)}</span>
                            </div>
                            {desglose.totalRetencion > 0 && (
                              <div className="pp-row">
                                <span>Retención registrada</span>
                                <span className="pp-val pp-ret">- {formatMoney(desglose.totalRetencion)}</span>
                              </div>
                            )}
                            <div className="pp-row">
                              <span>Ya pagado</span>
                              <span className="pp-val pp-pagado">- {formatMoney(factura.monto_pagado)}</span>
                            </div>
                            {/* Debe coincidir con el tope que valida el formulario (maxPagable) */}
                            <div className="pp-row pp-saldo-row">
                              <span>Disponible a pagar</span>
                              <span className="pp-val pp-saldo">{formatMoney(desglose.maxPagable)}</span>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="factura-monto-input">
                        <label>Monto a pagar:</label>
                        <div className="input-money">
                          <span className="currency">$</span>
                          <input
                            type="number"
                            value={montosPago[factura.id] || ''}
                            onChange={(e) => handleMontoChange(factura.id, e.target.value)}
                            min="0"
                            step="0.01"
                            placeholder="0"
                          />
                        </div>
                        <button
                          type="button"
                          className="btn-pago-total"
                          onClick={() => handleMontoChange(factura.id, desglose.maxPagable)}
                        >
                          Pago Total
                        </button>
                      </div>

                      {/* Aviso: el monto supera el saldo disponible */}
                      {((parseFloat(montosPago[factura.id]) || 0) > topePagableFactura(factura) + 1) && (
                        <div className="pago-monto-excedido" style={{ color: '#dc2626', fontSize: '0.8rem', marginTop: 6, fontWeight: 600 }}>
                          ⚠️ El monto supera el saldo disponible ({formatMoney(topePagableFactura(factura))}). Ajústalo para poder registrar el pago.
                        </div>
                      )}

                      {/* Preview: si es pago parcial, mostrar qué queda */}
                      {(montosPago[factura.id] > 0 && montosPago[factura.id] < desglose.maxPagable) && (
                        <div className="pago-parcial-preview">
                          <span className="pp-badge">Pago Parcial</span>
                          <span>Después de este pago quedará: <strong>{formatMoney(desglose.maxPagable - (parseFloat(montosPago[factura.id]) || 0))}</strong> pendiente al proveedor</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Resumen con retenciones calculadas dinámicamente */}
              {facturas.some(f => {
                const d = calcularDesglose(f);
                return d.totalRetencion > 0 || d.tieneIva;
              }) && (
                <div className="resumen-retenciones">
                  {facturas.some(f => calcularDesglose(f).tieneIva) && (
                    <div className="retencion-line">
                      <span className="label">Total IVA incluido:</span>
                      <span className="valor">{formatMoney(facturas.reduce((s, f) => s + calcularDesglose(f).ivaValor, 0))}</span>
                    </div>
                  )}
                  {facturas.some(f => calcularDesglose(f).totalRetencion > 0) && (
                    <div className="retencion-line">
                      <span className="label">Total retenciones:</span>
                      <span className="valor negativo">- {formatMoney(facturas.reduce((s, f) => s + calcularDesglose(f).totalRetencion, 0))}</span>
                    </div>
                  )}
                  <p className="retencion-nota">
                    * La retención se calcula sobre el subtotal antes de IVA. El proveedor recibirá el total menos retenciones.
                  </p>
                </div>
              )}

              <div className="total-pago-summary">
                <span className="label">Total a Pagar:</span>
                <span className="valor">{formatMoney(totalPago)}</span>
              </div>
            </div>

            {/* Información del pago */}
            <div className="datos-pago-section">
              <h3>Información del Pago</h3>

              <div className="form-row">
                <div className="form-group">
                  <label>Fecha del Pago *</label>
                  <input
                    type="date"
                    name="fecha_pago"
                    value={formData.fecha_pago}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Método de Pago *</label>
                  <select
                    name="metodo_pago"
                    value={formData.metodo_pago}
                    onChange={handleChange}
                    required
                  >
                    <option value="transferencia">Transferencia Bancaria</option>
                    <option value="efectivo">Efectivo</option>
                    <option value="cheque">Cheque</option>
                    <option value="tarjeta">Tarjeta</option>
                    <option value="nequi">Nequi</option>
                    <option value="daviplata">Daviplata</option>
                    <option value="otro">Otro</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Referencia / Número Transacción</label>
                  <input
                    type="text"
                    name="referencia"
                    value={formData.referencia}
                    onChange={handleChange}
                    placeholder="Ej: 123456789"
                  />
                </div>

                <div className="form-group">
                  <label>Banco</label>
                  <input
                    type="text"
                    name="banco"
                    value={formData.banco}
                    onChange={handleChange}
                    placeholder="Ej: Bancolombia"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Notas / Observaciones</label>
                <textarea
                  name="notas"
                  value={formData.notas}
                  onChange={handleChange}
                  placeholder="Información adicional del pago..."
                  rows="2"
                />
              </div>
            </div>

            {/* Evidencia del pago - OBLIGATORIO */}
            <div className="evidencia-section" onPaste={handlePaste} tabIndex={-1}>
              <h3>Comprobante de Pago *</h3>
              <p className="evidencia-nota">Es obligatorio adjuntar una foto o documento del comprobante</p>

              <div className="upload-container">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
                  onChange={handleFileChange}
                  className="file-input-hidden"
                  id="pago-evidencia"
                />

                {!archivoEvidencia ? (
                  <label htmlFor="pago-evidencia" className="upload-label" onPaste={handlePaste}>
                    <div className="upload-icon">📎</div>
                    <div className="upload-text">
                      <span>Seleccionar Foto o Documento</span>
                      <small>JPG, PNG, GIF, WebP o PDF (máx. 10MB)</small>
                    </div>
                    <div className="upload-paste-hint">
                      <small style={{ color: '#6366f1', fontWeight: 600 }}>o pega una imagen con Ctrl+V</small>
                    </div>
                  </label>
                ) : (
                  <div className="archivo-seleccionado">
                    {archivoEvidencia.type.startsWith('image/') && (
                      <img
                        src={URL.createObjectURL(archivoEvidencia)}
                        alt="Preview"
                        style={{ maxWidth: '100%', maxHeight: 120, borderRadius: 6, marginBottom: 8, objectFit: 'contain', border: '1px solid #e2e8f0' }}
                      />
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
                      <span className="archivo-icono">
                        {archivoEvidencia.type.includes('pdf') ? '📄' : '🖼️'}
                      </span>
                      <div className="archivo-info">
                        <span className="archivo-nombre">{archivoEvidencia.name}</span>
                        <span className="archivo-size">{formatFileSize(archivoEvidencia.size)}</span>
                      </div>
                      <button type="button" className="btn-quitar-archivo" onClick={quitarArchivo}>
                        ×
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn-cancelar" onClick={onClose}>
              Cancelar
            </button>
            <button
              type="submit"
              className="btn-registrar-pago"
              disabled={guardando || !archivoEvidencia || totalPago <= 0}
            >
              {guardando ? 'Registrando...' : `💳 Registrar Pago ${formatMoney(totalPago)}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ModalRegistrarPago;
